import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AlertTriangle, Building2, LayoutList, CalendarDays, Grid3x3, Tag, BarChart3, Crown, QrCode, Package, Users, Star, HeartPulse } from 'lucide-react';
import '../../../shared/components/layout/StoreDashboardLayout.css';
import '../../auth/components/Hero/Hero.css';
import StoreAnalytics from '../../analytics/pages/StoreAnalytics';
import StoreHealth from '../../store-health/pages/StoreHealth';
import StoreManage from '../../hierarchy/pages/StoreManage';
import StoreAppointments from '../../appointments/pages/StoreAppointments';
import StoreLiveView from './StoreLiveView';
import StoreOffers from '../../offers/pages/StoreOffers';
import AiBadge from '../../offers/components/AiBadge';
import CustomerReviews from '../../reviews/pages/CustomerReviews';
import MembershipsOverviewPage from '../../membership/pages/MembershipsOverviewPage';
import MembershipsPage from '../../membership/pages/MembershipsPage';
import MembershipCreatorPage from '../../membership/pages/MembershipCreatorPage';
import MembershipTypeEditorPage from '../../membership/pages/MembershipTypeEditorPage';
import MembershipCustomerDetailPage from '../../membership/pages/MembershipCustomerDetailPage';
import StoreServicesCatalog from '../../services/pages/StoreServicesCatalog';
import EmployeesWorkspace from '../../employees/pages/EmployeesWorkspace';
import { env } from '../../../config/env';
import { resolveLegacyMembershipRedirect, resolveMembershipRoute } from '../../membership/routes';
import { StoreQRPosters } from '../../qr-posters';
import { useDispatch, useSelector } from 'react-redux';
import { setRoute, setStoreMallUuid, setStoreName } from '../../../app/providers/store';
import EmptyState from '../../../shared/components/ui/EmptyState';
import {
  PROFILE_STORES_APPOINTMENTS_BOOKINGS,
  PROFILE_STORES_OFFERS,
  PROFILE_STORES_SELF,
  PROFILE_SECTIONS_SELF,
  QUEUE_STORE_LIVE_STATS
} from '../../../shared/constants/endpoints';
import { parseJwt } from '../../../shared/utils/jwt';
import { logger } from '../../../shared/utils/logger.js';
import { computeStoreProfileReadiness, persistStoreProfileReadiness } from '../../../shared/utils/storeProfileReadiness';
import { normalizeDashboardTab } from './dashboardTabs';

const ANALYTICS_ACTIVE_MODULE_STORAGE_KEY = 'dashboardAnalyticsActiveModule';
const DASHBOARD_ACTIVE_TAB_STORAGE_KEY = 'dashboardActiveTabV2';

const isSalonOrClinicMerchant = (...values) => values.some((value) => {
  const normalized = String(value || '').toUpperCase();
  return normalized.includes('SALON') || normalized.includes('CLINIC');
});

const PLAN_CATALOG = {
  free: {
    label: 'Free',
    modules: {
      queue: true,
      queueAnalytics: true,
      peakAnalytics: false,
      abandonmentAnalytics: true,
      bottleneckAnalytics: true,
      offersAnalytics: false,
      appointmentsAnalytics: false,
      customerBehaviorAnalytics: true,
      staffingAnalytics: true,
      whatIfAnalytics: true,
      anomalyAnalytics: true,
      appointments: false,
      offers: false,
      heatmap: false,
      priorityPass: false,
      multiStore: false
    }
  },
  starter: {
    label: 'Starter',
    modules: {
      queue: true,
      queueAnalytics: true,
      peakAnalytics: false,
      abandonmentAnalytics: true,
      bottleneckAnalytics: true,
      offersAnalytics: false,
      appointmentsAnalytics: true,
      customerBehaviorAnalytics: true,
      staffingAnalytics: true,
      whatIfAnalytics: true,
      anomalyAnalytics: true,
      appointments: true,
      offers: false,
      heatmap: false,
      priorityPass: false,
      multiStore: false
    }
  },
  growth: {
    label: 'Growth',
    modules: {
      queue: true,
      queueAnalytics: true,
      peakAnalytics: true,
      abandonmentAnalytics: true,
      bottleneckAnalytics: true,
      offersAnalytics: true,
      appointmentsAnalytics: true,
      customerBehaviorAnalytics: true,
      staffingAnalytics: true,
      whatIfAnalytics: true,
      anomalyAnalytics: true,
      appointments: true,
      offers: true,
      heatmap: true,
      priorityPass: true,
      multiStore: false
    }
  },
  pro: {
    label: 'Pro',
    modules: {
      queue: true,
      queueAnalytics: true,
      peakAnalytics: true,
      abandonmentAnalytics: true,
      bottleneckAnalytics: true,
      offersAnalytics: true,
      appointmentsAnalytics: true,
      customerBehaviorAnalytics: true,
      staffingAnalytics: true,
      whatIfAnalytics: true,
      anomalyAnalytics: true,
      appointments: true,
      offers: true,
      heatmap: true,
      priorityPass: true,
      multiStore: true
    }
  }
};

const resolvePlanKey = (rawPlan) => {
  const value = String(rawPlan || '').toLowerCase();
  if (value.includes('pro')) return 'pro';
  if (value.includes('growth')) return 'growth';
  if (value.includes('starter') || value.includes('start')) return 'starter';
  return 'free';
};

const readCapabilityFlag = (tokenClaims, storeProfile, flagName, fallbackValue = false) => {
  const tokenValue = tokenClaims?.[flagName];
  if (typeof tokenValue === 'boolean') {
    return tokenValue;
  }

  const profileValue = storeProfile?.[flagName];
  if (typeof profileValue === 'boolean') {
    return profileValue;
  }

  return Boolean(fallbackValue);
};

const StoreDashboard = ({ request, pushActivity, onToast, routePath, membershipEnabled = false }) => {
  const storeToken = useSelector((state) => state.store.storeToken);
  const storeUuid = useSelector((state) => state.store.storeUuid);
  const storeMallUuid = useSelector((state) => state.store.storeMallUuid);
  const storeName = useSelector((state) => state.store.storeName);
  const dispatch = useDispatch();
  const isManageRoute = String(routePath || '').startsWith('/store/manage');
  const isMembershipsRoute = String(routePath || '').startsWith('/store/memberships');

  const navigate = useCallback((path, { replace } = {}) => {
    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
    dispatch(setRoute(path || '/'));
  }, [dispatch]);

  const [activeTab, setActiveTabState] = useState(() => {
    const saved = localStorage.getItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY);
    return normalizeDashboardTab(saved, env.servicesEmployeesRolesEnabled);
  });
  const setActiveTab = useCallback((tab) => {
    const normalizedTab = normalizeDashboardTab(tab, env.servicesEmployeesRolesEnabled);
    setActiveTabState(normalizedTab);
    localStorage.setItem(DASHBOARD_ACTIVE_TAB_STORAGE_KEY, normalizedTab);
  }, []);
  const tokenClaims = useMemo(() => parseJwt(storeToken), [storeToken]);
  const [sections, setSections] = useState([]);
  const [storeProfile, setStoreProfile] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [liveLoadError, setLiveLoadError] = useState('');
  const [isLiveRefreshing, setIsLiveRefreshing] = useState(false);
  const [appointmentsTodayCount, setAppointmentsTodayCount] = useState(0);
  const [activeOffersCount, setActiveOffersCount] = useState(0);
  const servicesModuleGloballyEnabled = env.servicesEmployeesRolesEnabled;
  const servicesCatalogEnabled = servicesModuleGloballyEnabled
    && readCapabilityFlag(tokenClaims, storeProfile, 'servicesEnabled', false);
  const staffingWorkspaceEnabled = servicesModuleGloballyEnabled
    && readCapabilityFlag(tokenClaims, storeProfile, 'staffingEnabled', false);
  const usesAppointmentLanguage = isSalonOrClinicMerchant(
    tokenClaims?.clientType,
    storeProfile?.clientType,
    storeProfile?.storeCategory,
    storeProfile?.merchantType,
    storeProfile?.storeType
  );
  const merchantPortalLabels = usesAppointmentLanguage
    ? {
      appointments: 'Appointments',
      appointmentSettings: 'Appointment Settings',
      serviceCatalog: 'Services'
    }
    : {
      appointments: 'Bookings',
      appointmentSettings: 'Bookings settings',
      serviceCatalog: 'Catalogue'
    };
  const dashboardNavGroups = [
    { group: 'Operate', items: [
      { key: 'live', label: 'Live Queue', icon: <LayoutList size={16} /> },
      { key: 'appointments', label: merchantPortalLabels.appointments, icon: <CalendarDays size={16} /> },
      { key: 'services', label: merchantPortalLabels.appointmentSettings, icon: <Building2 size={16} /> },
      { key: 'service-catalog', label: merchantPortalLabels.serviceCatalog, icon: <Package size={16} /> },
      ...(staffingWorkspaceEnabled
        ? [{ key: 'employees', label: 'Employees', icon: <Users size={16} /> }]
        : []),
      { key: 'manage', label: 'Sections', icon: <Grid3x3 size={16} /> },
    ]},
    { group: 'Grow', items: [
      { key: 'offers', label: 'Offers', icon: <Tag size={16} />, badge: <AiBadge /> },
      { key: 'memberships', label: 'Memberships', icon: <Crown size={16} /> },
      { key: 'customer-reviews', label: 'Reviews', icon: <Star size={16} /> },
      { key: 'store-health', label: 'Store Health', icon: <HeartPulse size={16} />, badge: <AiBadge /> },
      { key: 'analytics', label: 'Analytics', icon: <BarChart3 size={16} /> },
      { key: 'qr-posters', label: 'QR Posters', icon: <QrCode size={16} /> }
    ]},
  ];
  const livePollInFlightRef = useRef(false);
  const isStoreLoggedIn = Boolean(storeToken || storeUuid);
  const effectiveTab = isMembershipsRoute ? 'memberships' : activeTab;
  const legacyMembershipRedirect = resolveLegacyMembershipRedirect(routePath);

  useEffect(() => {
    if (!legacyMembershipRedirect || legacyMembershipRedirect === routePath) {
      return;
    }
    navigate(legacyMembershipRedirect, { replace: true });
  }, [legacyMembershipRedirect, navigate, routePath]);

  const selectDashboardTab = useCallback((tabKey) => {
    if (tabKey === 'memberships') {
      navigate('/store/memberships');
    } else if (isManageRoute || isMembershipsRoute) {
      navigate('/store');
    }
    setActiveTab(tabKey);
  }, [isManageRoute, isMembershipsRoute, navigate, setActiveTab]);

  const fetchSections = useCallback(async () => {
    if (!isStoreLoggedIn || !storeUuid) {
      return;
    }
    try {
      const response = await request(PROFILE_SECTIONS_SELF, {
        method: 'GET',
        requestName: 'sections'
      });
      const list = response?.data;
      const sectionList = Array.isArray(list) ? list : [];
      const dedupedSections = Array.from(
        sectionList.reduce((accumulator, section) => {
          const uuid = String(section?.sectionUuid || '').trim();
          if (!uuid) {
            return accumulator;
          }
          if (!accumulator.has(uuid)) {
            accumulator.set(uuid, section);
          }
          return accumulator;
        }, new Map()).values()
      );
      setSections(dedupedSections);
      if (!storeMallUuid && sectionList[0]?.mallUuid) {
        dispatch(setStoreMallUuid(sectionList[0].mallUuid));
      }
    } catch (error) {
      return;
    }
  }, [dispatch, isStoreLoggedIn, storeMallUuid, storeUuid, request]);

  useEffect(() => {
    if (isStoreLoggedIn && storeUuid) {
      request(PROFILE_STORES_SELF, {
        method: 'GET',
        requestName: 'stores'
      })
        .then((response) => {
          const storeData = response?.data || response;
          setStoreProfile(storeData);
          const name = storeData?.storeName || '';
          if (name) {
            dispatch(setStoreName(name));
          }
        })
        .catch((err) => {
          logger.error('StoreDashboard', 'Error fetching store info', {
            source: { file: 'features/queue-management/pages/index.js', method: 'fetchStoreProfile' },
            error: err instanceof Error
              ? { name: err.name, message: err.message, stack: err.stack }
              : String(err)
          });
        });
      fetchSections();
    }
  }, [dispatch, isStoreLoggedIn, storeUuid, storeToken, request, fetchSections]);

  const fetchLiveStats = useCallback(async () => {
    if (!isStoreLoggedIn || !storeUuid || !storeToken || effectiveTab !== 'live') {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    if (livePollInFlightRef.current) {
      return;
    }

    livePollInFlightRef.current = true;
    setIsLiveRefreshing(true);
    try {
      const response = await request(QUEUE_STORE_LIVE_STATS, {
        method: 'GET',
        requestName: 'store-live-stats'
      });
      const payload = response?.data || null;
      setLiveStats(payload ? { ...payload, _fetchedAt: Date.now() } : null);
      setLiveLoadError('');
    } catch (error) {
      setLiveLoadError('Unable to load live operations data.');
      logger.error('StoreDashboard', 'Failed to fetch live stats', {
        source: { file: 'features/queue-management/pages/index.js', method: 'fetchLiveStats' },
        error: error instanceof Error
          ? { name: error.name, message: error.message, stack: error.stack }
          : String(error)
      });
    } finally {
      setIsLiveRefreshing(false);
      livePollInFlightRef.current = false;
    }
  }, [effectiveTab, isStoreLoggedIn, request, storeToken, storeUuid]);

  const fetchOverviewSummary = useCallback(async () => {
    if (!isStoreLoggedIn || !storeUuid || !storeToken) {
      setAppointmentsTodayCount(0);
      setActiveOffersCount(0);
      return;
    }
    // Only call the APIs for modules explicitly granted in the token or store profile.
    // Checking the token directly here avoids a dependency on resolvedMerchantModules,
    // which is declared later in the component body.
    const claims = parseJwt(storeToken);
    const hasAppointments = typeof claims?.appointmentsEnabled === 'boolean'
      ? claims.appointmentsEnabled
      : typeof storeProfile?.appointmentsEnabled === 'boolean'
        ? storeProfile.appointmentsEnabled
        : false;
    const hasOffers = typeof claims?.offersEnabled === 'boolean'
      ? claims.offersEnabled
      : typeof storeProfile?.offersEnabled === 'boolean'
        ? storeProfile.offersEnabled
        : false;
    if (!hasAppointments && !hasOffers) {
      setAppointmentsTodayCount(0);
      setActiveOffersCount(0);
      return;
    }
    try {
      const [appointmentsResponse, offersResponse] = await Promise.all([
        request(PROFILE_STORES_APPOINTMENTS_BOOKINGS('today'), {
          method: 'GET',
          requestName: 'dashboard-overview-appointments'
        }),
        request(PROFILE_STORES_OFFERS, {
          method: 'GET',
          requestName: 'dashboard-overview-offers'
        })
      ]);

      const appointmentsPayload = appointmentsResponse?.data;
      const appointments = Array.isArray(appointmentsPayload)
        ? appointmentsPayload
        : Array.isArray(appointmentsPayload?.bookings)
          ? appointmentsPayload.bookings
          : [];
      setAppointmentsTodayCount(appointments.length);

      const offersPayload = offersResponse?.data;
      const offers = Array.isArray(offersPayload) ? offersPayload : [];
      const activeOffers = offers.filter((offer) => {
        const lifecycle = String(offer?.lifecycle || '').toLowerCase();
        const status = String(offer?.status || offer?.statusLabel || '').toLowerCase();
        return lifecycle === 'live' || status === 'active' || status === 'running';
      });
      setActiveOffersCount(activeOffers.length);
    } catch (_error) {
      setAppointmentsTodayCount(0);
      setActiveOffersCount(0);
    }
  }, [isStoreLoggedIn, request, storeProfile, storeToken, storeUuid]);

  useEffect(() => {
    if (!isStoreLoggedIn || !storeUuid || !storeToken) {
      livePollInFlightRef.current = false;
      setLiveStats(null);
      return;
    }
    if (effectiveTab !== 'live') {
      return;
    }

    const pollIfVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      fetchLiveStats();
    };

    pollIfVisible();
    const intervalId = setInterval(pollIfVisible, 10000);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', pollIfVisible);
    }

    return () => {
      clearInterval(intervalId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', pollIfVisible);
      }
    };
  }, [effectiveTab, fetchLiveStats, isStoreLoggedIn, storeToken, storeUuid]);

  useEffect(() => {
    if (effectiveTab !== 'live') {
      return;
    }
    fetchOverviewSummary();
  }, [effectiveTab, fetchOverviewSummary]);

  const tokenPlanClaim = String(
    tokenClaims?.currentPlan
    || tokenClaims?.plan
    || tokenClaims?.subscriptionPlan
    || ''
  ).trim();

  const resolvedRawPlan = (
    tokenPlanClaim
    || String(
      storeProfile?.currentPlan
      || storeProfile?.plan
      || storeProfile?.planType
      || storeProfile?.subscriptionTier
      || storeProfile?.subscriptionPlan
      || 'free'
    )
  );
  const resolvedAnalyticsPlan = resolvePlanKey(resolvedRawPlan);
  const resolvedAnalyticsLocation = String(
    storeProfile?.location
    || [storeProfile?.city, storeProfile?.state].filter(Boolean).join(', ')
    || 'India'
  );
  const resolvedAnalyticsType = String(
    storeProfile?.merchantType
    || storeProfile?.storeCategory
    || storeProfile?.storeType
    || 'Queue merchant'
  );
  const defaultPlanModules = PLAN_CATALOG[resolvedAnalyticsPlan]?.modules || PLAN_CATALOG.free.modules;
  const pickModuleFlag = (tokenValue, profileValue, fallbackValue = false) => {
    if (typeof tokenValue === 'boolean') return tokenValue;
    if (typeof profileValue === 'boolean') return profileValue;
    return Boolean(fallbackValue);
  };
  const resolvedMerchantModules = {
    queue: pickModuleFlag(tokenClaims?.queueManagementEnabled, storeProfile?.queueManagementEnabled, defaultPlanModules.queue),
    // appointments and offers are opt-in — only enable if the token or profile explicitly grants them.
    // Never fall back to the plan catalog for these: a retail store on the "starter" plan must not
    // accidentally get appointments UI just because the plan catalog lists it as included.
    appointments: pickModuleFlag(tokenClaims?.appointmentsEnabled, storeProfile?.appointmentsEnabled, false),
    services: servicesCatalogEnabled,
    staffing: staffingWorkspaceEnabled,
    offers: pickModuleFlag(tokenClaims?.offersEnabled, storeProfile?.offersEnabled, false),
    reviews: pickModuleFlag(tokenClaims?.reviewsEnabled, storeProfile?.reviewsEnabled, true),
    heatmap: pickModuleFlag(tokenClaims?.heatmapEnabled, storeProfile?.heatmapEnabled, defaultPlanModules.heatmap),
    queueAnalytics: pickModuleFlag(
      tokenClaims?.queueAnalyticsEnabled,
      storeProfile?.queueAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.queueManagementEnabled, storeProfile?.queueManagementEnabled, defaultPlanModules.queueAnalytics)
    ),
    peakAnalytics: pickModuleFlag(
      tokenClaims?.peakAnalyticsEnabled,
      storeProfile?.peakAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.heatmapEnabled, storeProfile?.heatmapEnabled, defaultPlanModules.peakAnalytics)
    ),
    abandonmentAnalytics: pickModuleFlag(
      tokenClaims?.abandonmentAnalyticsEnabled,
      storeProfile?.abandonmentAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.queueManagementEnabled, storeProfile?.queueManagementEnabled, defaultPlanModules.abandonmentAnalytics)
    ),
    bottleneckAnalytics: pickModuleFlag(
      tokenClaims?.bottleneckAnalyticsEnabled,
      storeProfile?.bottleneckAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.queueAnalyticsEnabled, storeProfile?.queueAnalyticsEnabled, defaultPlanModules.bottleneckAnalytics)
    ),
    offersAnalytics: pickModuleFlag(
      tokenClaims?.offersAnalyticsEnabled,
      storeProfile?.offersAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.offersEnabled, storeProfile?.offersEnabled, defaultPlanModules.offersAnalytics)
    ),
    appointmentsAnalytics: pickModuleFlag(
      tokenClaims?.appointmentsAnalyticsEnabled,
      storeProfile?.appointmentsAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.appointmentsEnabled, storeProfile?.appointmentsEnabled, defaultPlanModules.appointmentsAnalytics)
    ),
    customerBehaviorAnalytics: pickModuleFlag(
      tokenClaims?.customerBehaviorAnalyticsEnabled,
      storeProfile?.customerBehaviorAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.queueAnalyticsEnabled, storeProfile?.queueAnalyticsEnabled, defaultPlanModules.customerBehaviorAnalytics)
    ),
    staffingAnalytics: pickModuleFlag(
      tokenClaims?.staffingAnalyticsEnabled,
      storeProfile?.staffingAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.bottleneckAnalyticsEnabled, storeProfile?.bottleneckAnalyticsEnabled, defaultPlanModules.staffingAnalytics)
    ),
    whatIfAnalytics: pickModuleFlag(
      tokenClaims?.whatIfAnalyticsEnabled,
      storeProfile?.whatIfAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.bottleneckAnalyticsEnabled, storeProfile?.bottleneckAnalyticsEnabled, defaultPlanModules.whatIfAnalytics)
    ),
    anomalyAnalytics: pickModuleFlag(
      tokenClaims?.anomalyAnalyticsEnabled,
      storeProfile?.anomalyAnalyticsEnabled,
      pickModuleFlag(tokenClaims?.queueAnalyticsEnabled, storeProfile?.queueAnalyticsEnabled, defaultPlanModules.anomalyAnalytics)
    ),
    priorityPass: pickModuleFlag(tokenClaims?.priorityPassEnabled, storeProfile?.priorityPassEnabled, defaultPlanModules.priorityPass),
    memberships: pickModuleFlag(
      tokenClaims?.membershipEnabled ?? tokenClaims?.membership_enabled,
      storeProfile?.membershipEnabled ?? storeProfile?.membership_enabled,
      membershipEnabled
    ),
    multiStore: pickModuleFlag(tokenClaims?.multiStoreEnabled, storeProfile?.multiStoreEnabled, defaultPlanModules.multiStore)
  };
  const hasAnyAnalyticsAccess = Boolean(
    resolvedMerchantModules.queueAnalytics
    || resolvedMerchantModules.peakAnalytics
    || resolvedMerchantModules.abandonmentAnalytics
    || resolvedMerchantModules.bottleneckAnalytics
    || resolvedMerchantModules.offersAnalytics
    || resolvedMerchantModules.appointmentsAnalytics
    || resolvedMerchantModules.customerBehaviorAnalytics
    || resolvedMerchantModules.staffingAnalytics
    || resolvedMerchantModules.whatIfAnalytics
    || resolvedMerchantModules.anomalyAnalytics
  );
  const hasAppointmentsAccess = Boolean(resolvedMerchantModules.appointments);
  const hasOffersAccess = Boolean(resolvedMerchantModules.offers);
  const hasReviewsAccess = Boolean(resolvedMerchantModules.reviews);
  const isAnalyticsTabDisabled = !hasAnyAnalyticsAccess;
  const isAppointmentsTabDisabled = !hasAppointmentsAccess;
  const isServicesCatalogTabDisabled = !servicesCatalogEnabled;
  const isEmployeesTabDisabled = !staffingWorkspaceEnabled;
  const isOffersTabDisabled = !hasOffersAccess;
  const isReviewsTabDisabled = !hasReviewsAccess;
  const isMembershipsTabDisabled = !resolvedMerchantModules.memberships;
  // Live Queue and Sections are both facets of queue management, so they gate together
  // on the queueManagementEnabled claim (there is no separate "sections" flag).
  const isQueueManagementDisabled = !resolvedMerchantModules.queue;
  const isTabDisabled = (tabKey) => {
    if (tabKey === 'live') return isQueueManagementDisabled;
    if (tabKey === 'manage') return isQueueManagementDisabled;
    if (tabKey === 'qr-posters') return isQueueManagementDisabled;
    if (tabKey === 'analytics') return isAnalyticsTabDisabled;
    if (tabKey === 'appointments') return isAppointmentsTabDisabled;
    if (tabKey === 'services') return isAppointmentsTabDisabled;
    if (tabKey === 'service-catalog') return isServicesCatalogTabDisabled;
    if (tabKey === 'employees') return isEmployeesTabDisabled;
    if (tabKey === 'offers') return isOffersTabDisabled;
    if (tabKey === 'customer-reviews') return isReviewsTabDisabled;
    if (tabKey === 'memberships') return isMembershipsTabDisabled;
    return false;
  };
  const visibleDashboardNavGroups = dashboardNavGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((tab) => !isTabDisabled(tab.key))
    }))
    .filter((group) => group.items.length > 0);
  const openOfferAnalytics = useCallback(() => {
    if (!hasAnyAnalyticsAccess) {
      onToast?.({ message: 'Enable analytics modules to access this tab.', variant: 'info' });
      return;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ANALYTICS_ACTIVE_MODULE_STORAGE_KEY, 'offers');
    }
    selectDashboardTab('analytics');
  }, [hasAnyAnalyticsAccess, onToast, selectDashboardTab]);

  useEffect(() => {
    // Nav order used to pick the landing tab when the active one is disabled. Built inside
    // the effect so its dependencies are just the flag booleans already listed below.
    const orderedDashboardTabs = [
      ['live', isQueueManagementDisabled],
      ['appointments', isAppointmentsTabDisabled],
      ['services', isAppointmentsTabDisabled],
      ['service-catalog', isServicesCatalogTabDisabled],
      ['employees', isEmployeesTabDisabled],
      ['manage', isQueueManagementDisabled],
      ['offers', isOffersTabDisabled],
      ['memberships', isMembershipsTabDisabled],
      ['customer-reviews', isReviewsTabDisabled],
      ['store-health', false],
      ['analytics', isAnalyticsTabDisabled],
      ['qr-posters', isQueueManagementDisabled],
    ];
    const activeTabEntry = orderedDashboardTabs.find(([key]) => key === activeTab);
    const activeTabDisabled = Boolean(activeTabEntry && activeTabEntry[1]);
    if (activeTabDisabled) {
      // Land on the first enabled tab in nav order rather than always falling back to
      // 'live' — 'live' (and 'manage'/'qr-posters') can now be disabled when
      // queueManagementEnabled === false. If every tab is disabled, fall back to 'live'
      // as a terminal default (no-op re-set, so no render loop).
      const firstEnabledEntry = orderedDashboardTabs.find(([, disabled]) => !disabled);
      setActiveTab(firstEnabledEntry ? firstEnabledEntry[0] : 'live');
      if (isManageRoute || isMembershipsRoute) {
        navigate('/store');
      }
    }
  }, [activeTab, isQueueManagementDisabled, isAnalyticsTabDisabled, isAppointmentsTabDisabled, isEmployeesTabDisabled, isMembershipsTabDisabled, isOffersTabDisabled, isReviewsTabDisabled, isServicesCatalogTabDisabled, isManageRoute, isMembershipsRoute, navigate, setActiveTab]);

  const profileReadiness = useMemo(
    () => computeStoreProfileReadiness(storeProfile || {}),
    [storeProfile]
  );
  useEffect(() => {
    persistStoreProfileReadiness(storeUuid, profileReadiness);
  }, [profileReadiness, storeUuid]);
  if (!isStoreLoggedIn) return null;

  return (
    <div className="store-dashboard-container">
      <div className="dashboard-main-layout">
        <aside className="dashboard-sidebar">
          {visibleDashboardNavGroups.map((group) => (
            <div key={group.group} className="dashboard-nav-group">
              <div className="dashboard-nav-section">{group.group}</div>
              {group.items.map((tab) => {
                const isActive = effectiveTab === tab.key;
                return (
                  <div
                    key={tab.key}
                    className="sidebar-tab-wrapper"
                  >
                    <button
                      type="button"
                      className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        selectDashboardTab(tab.key);
                      }}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className="sidebar-nav-icon">{tab.icon}</span>
                      <span className="sidebar-nav-label">{tab.label}</span>
                      {tab.badge ? <span className="sidebar-nav-badge">{tab.badge}</span> : null}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </aside>

        <div className="dashboard-content-area">
          {effectiveTab === 'live' && !isQueueManagementDisabled && (
            liveLoadError && !liveStats ? (
              <EmptyState
                icon={<AlertTriangle size={20} />}
                title="Couldn't load live view"
                description="There was a problem fetching live operations data. Try again."
                action={{ label: 'Retry', onClick: fetchLiveStats }}
              />
            ) : (
              <StoreLiveView
                liveStats={liveStats}
                modules={resolvedMerchantModules}
                clientType={tokenClaims?.clientType}
                appointmentsTodayCount={appointmentsTodayCount}
                activeOffersCount={activeOffersCount}
                onSelectTab={selectDashboardTab}
                isLiveRefreshing={isLiveRefreshing}
                liveLastFetchedAt={liveStats?._fetchedAt || null}
                onRefreshLive={fetchLiveStats}
                request={request}
                onToast={onToast}
              />
            )
          )}

          {effectiveTab === 'analytics' && hasAnyAnalyticsAccess && (
            <StoreAnalytics
              request={request}
              storeToken={storeToken}
              storeUuid={storeUuid}
              merchantModules={resolvedMerchantModules}
              merchantInfo={{
                name: storeName || storeProfile?.storeName || 'Store',
                location: resolvedAnalyticsLocation,
                type: resolvedAnalyticsType,
                plan: resolvedAnalyticsPlan,
                icon: <Building2 size={16} />
              }}
            />
          )}

          {effectiveTab === 'store-health' && (
            <StoreHealth
              request={request}
              storeToken={storeToken}
              onToast={onToast}
            />
          )}

          {effectiveTab === 'manage' && !isQueueManagementDisabled && (
            <StoreManage
              sections={sections}
              request={request}
              onToast={onToast}
              onSectionsUpdated={fetchSections}
            />
          )}

          {effectiveTab === 'appointments' && hasAppointmentsAccess && (
            <StoreAppointments
              request={request}
              onToast={onToast}
              initialTab="today"
              showWorkspaceTabs={false}
              headerTitle={merchantPortalLabels.appointments}
              onReviewDrafts={() => {
                try { window.localStorage.setItem('offersActiveViewV2', 'ai'); } catch (error) { /* view falls back */ }
                setActiveTab('offers');
              }}
            />
          )}

          {effectiveTab === 'services' && hasAppointmentsAccess && (
            <StoreAppointments
              request={request}
              onToast={onToast}
              initialTab="schedule"
              showWorkspaceTabs={false}
              headerTitle={merchantPortalLabels.appointmentSettings}
            />
          )}

          {effectiveTab === 'service-catalog' && servicesCatalogEnabled && (
            <StoreServicesCatalog
              request={request}
              storeToken={storeToken}
              onToast={onToast}
              staffingEnabled={staffingWorkspaceEnabled}
              title={merchantPortalLabels.serviceCatalog}
            />
          )}

          {effectiveTab === 'employees' && staffingWorkspaceEnabled && (
            <EmployeesWorkspace onToast={onToast} enabled={staffingWorkspaceEnabled} />
          )}

          {effectiveTab === 'offers' && hasOffersAccess && (
            <StoreOffers request={request} onToast={onToast} onViewAnalytics={openOfferAnalytics} />
          )}

          {effectiveTab === 'customer-reviews' && hasReviewsAccess && (
            <CustomerReviews
              request={request}
              storeToken={storeToken}
              onToast={onToast}
              storeProfile={storeProfile}
            />
          )}

          {effectiveTab === 'qr-posters' && !isQueueManagementDisabled && (
            <StoreQRPosters sections={sections} storeProfile={storeProfile} onToast={onToast} />
          )}

          {effectiveTab === 'memberships' && resolvedMerchantModules.memberships && (() => {
            const membershipRoute = resolveMembershipRoute(routePath);
            if (membershipRoute?.kind === 'create') {
              if (env.membershipsV2Enabled) {
                return (
                  <MembershipCreatorPage
                    routePath={routePath}
                    onNavigate={navigate}
                    storeName={storeName || storeProfile?.storeName || 'Store name'}
                  />
                );
              }
              return <MembershipTypeEditorPage membershipEnabled={resolvedMerchantModules.memberships} onNavigate={navigate} />;
            }
            if (membershipRoute?.kind === 'edit') {
              return <MembershipTypeEditorPage membershipEnabled={resolvedMerchantModules.memberships} typeUuid={membershipRoute.typeUuid} onNavigate={navigate} />;
            }
            if (membershipRoute?.kind === 'customer') {
              return <MembershipCustomerDetailPage membershipEnabled={resolvedMerchantModules.memberships} membershipUuid={membershipRoute.membershipUuid} onNavigate={navigate} />;
            }
            if (env.membershipsV2Enabled) {
              return <MembershipsPage membershipEnabled={resolvedMerchantModules.memberships} routePath={routePath} onNavigate={navigate} />;
            }
            return <MembershipsOverviewPage membershipEnabled={resolvedMerchantModules.memberships} onNavigate={navigate} />;
          })()}
        </div>
      </div>
    </div>
  );
};

export default StoreDashboard;
